import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import TimecardSummary from './components/TimecardSummary';
import RotationsList from './components/RotationsList';
import PayAnalytics from './components/PayAnalytics';
import TimecardRawData from './components/TimecardRawData';
import { getTimecards } from '../../services/apiService';

const TimecardDetail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const timecardId = searchParams.get('id');

  const [timecardData, setTimecardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    if (timecardId) {
      loadTimecardData();
    } else {
      setError('No timecard ID provided');
      setIsLoading(false);
    }
  }, [timecardId]);

  const loadTimecardData = async () => {
    // Guard against undefined/null/empty ID
    if (!timecardId || timecardId === 'undefined' || timecardId === 'null') {
      setError('No timecard ID provided in URL');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch timecard detail
      const response = await fetch(`http://localhost:8000/api/v1/timecards/${timecardId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load timecard: ${response.statusText}`);
      }

      const data = await response.json();
      setTimecardData(data);

    } catch (err) {
      console.error('Failed to load timecard data:', err);
      setError(err?.message || 'Failed to load timecard data');
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: 'summary', label: 'Overview', icon: 'FileText' },
    { id: 'analytics', label: 'Pay Analytics', icon: 'BarChart3' },
    { id: 'rotations', label: 'Rotations', icon: 'Plane' },
    { id: 'raw', label: 'Raw Data', icon: 'FileJson' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar />
        <main className="lg:ml-60 min-h-screen">
          <div className="container-custom py-6 md:py-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <Icon name="Loader2" className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
                <p className="text-muted-foreground">Loading timecard...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar />
        <main className="lg:ml-60 min-h-screen">
          <div className="container-custom py-6 md:py-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <Icon name="AlertCircle" className="w-12 h-12 text-error mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-2">Failed to Load Timecard</h3>
                <p className="text-muted-foreground mb-6">{error}</p>
                <button
                  onClick={() => navigate('/data-management')}
                  className="btn btn-primary"
                >
                  Back to Data Management
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!timecardData) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar />
        <main className="lg:ml-60 min-h-screen">
          <div className="container-custom py-6 md:py-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <Icon name="FileQuestion" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-2">Timecard Not Found</h3>
                <p className="text-muted-foreground mb-6">The timecard you're looking for doesn't exist.</p>
                <button
                  onClick={() => navigate('/data-management')}
                  className="btn btn-primary"
                >
                  Back to Data Management
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Dashboard', href: '/dashboard', icon: 'Home' },
    { label: 'Data Management', href: '/data-management', icon: 'Database' },
    { label: `Timecard ${timecardData.bid_period || timecardData.id}`, icon: 'FileText' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className="lg:ml-60 min-h-screen">
        <div className="container-custom py-6 md:py-8">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  Timecard {timecardData.bid_period}
                </h1>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name="User" className="w-4 h-4" />
                    {timecardData.employee_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Calendar" className="w-4 h-4" />
                    {timecardData.created_at ? new Date(timecardData.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
              <button
                onClick={() => navigate('/data-management')}
                className="btn btn-secondary flex items-center gap-2"
              >
                <Icon name="ArrowLeft" className="w-4 h-4" />
                Back to List
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-card rounded-lg border border-accent-cyan/20 overflow-hidden">
            <div className="border-b border-accent-cyan/20">
              <nav className="flex flex-wrap">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center gap-2 px-6 py-4 text-sm font-medium transition-smooth
                      border-b-2 ${
                        activeTab === tab.id
                          ? 'border-accent-cyan text-accent-cyan bg-accent-cyan/5'
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-tertiary'
                      }
                    `}
                  >
                    <Icon name={tab.icon} className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'summary' && (
                <TimecardSummary timecard={timecardData} />
              )}
              {activeTab === 'analytics' && (
                <PayAnalytics timecard={timecardData} />
              )}
              {activeTab === 'rotations' && (
                <RotationsList timecard={timecardData} />
              )}
              {activeTab === 'raw' && (
                <TimecardRawData timecard={timecardData} />
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TimecardDetail;
